import { BundleProps, ProductData } from 'utils/types';
import { StyledCashFrenzy } from './styles';
import {
  EBadgeType,
  EBundlesInternalViewModel,
  EGradientDirection,
  EProductPriority,
  ESaleDiscountDisplayType,
} from 'utils/enums';
import { useEffect, useState } from 'react';
import { AutoTextSize } from 'utils/autoTextSizeUtils';
import { BundlePrice } from 'components/BundlePrice';
import { BadgeData, IEmblemBadge } from 'components/Badge/types';
import { useGetBadge } from 'utils/useGetBadge';
import tooltipIcon from 'assets/SpinX/cash-tooltip.png';
import crossIcon from 'assets/SpinX/cf-button-cross.png';
import { SpecialOfferProps } from 'templates/SpecialOffer/types';
import { CashFrenzySaleBadge } from 'components/Badge/Sale/CashFrenzySaleBadge/CashFrenzySaleBadge';

interface ICashFrenzyTemplateProps {
  prices: BundleProps['prices'];
  productList: BundleProps['productList'];
  backgroundSrc: BundleProps['backgroundSrc'];
  backgroundColor: BundleProps['backgroundColor'];
  emblemBadges?: BundleProps['emblemBadges'];
  selectOffer?: SpecialOfferProps['selectOffer'];
  dataTestId?: string;
  timer?: string;
  badges?: BadgeData[];
  salePercentage?: number;
  salePercentageDisplayType?: ESaleDiscountDisplayType;
  isSpecialOffer?: boolean;
  inMobileMock?: boolean;
  isFree?: boolean;
  collectText?: string;
  buttonSuffixImage?: string;
  buttonSuffixAnimation?: string;
}

export const CashFrenzyTemplate: React.FC<ICashFrenzyTemplateProps> = ({
  prices,
  productList,
  backgroundSrc,
  backgroundColor = {
    colorOne: '#406cf9',
    colorTwo: '#6449ff',
    gradientDirection: EGradientDirection.TopToBottom,
  },
  badges = [],
  emblemBadges = [],
  salePercentage = 0,
  salePercentageDisplayType,
  isSpecialOffer = false,
  inMobileMock = false,
  isFree,
  collectText = 'Collect',
  timer,
  buttonSuffixImage = '',
  buttonSuffixAnimation = '',
  selectOffer,
}) => {
  const [mainProduct, setMainProduct] = useState<any>(null);
  const [subProducts, setSubProducts] = useState<ProductData[]>([]);
  const [emblemBadgeOld, setEmblemBadgeOld] = useState<any>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const emblemBadgeNew = useGetBadge<IEmblemBadge>(emblemBadges);

  useEffect(() => {
    if (Array.isArray(badges) && badges.length > 0) {
      const foundEmblem = badges?.find((b: BadgeData) => {
        return (
          b.badge?.type === EBadgeType.EMBLEM || b.type === EBadgeType.EMBLEM
        );
      });
      setEmblemBadgeOld(foundEmblem?.badge?.emblem || foundEmblem?.emblem);
    } else {
      setEmblemBadgeOld(null);
    }
  }, [badges]);

  const emblemBadge = emblemBadgeNew ?? emblemBadgeOld;

  useEffect(() => {
    let main = productList.find(
      (product) => product.priority === EProductPriority.MAIN,
    );
    const sub = productList.filter(
      (product) => product.priority === EProductPriority.SUB,
    );

    setMainProduct(main);
    setSubProducts(sub);
  }, [productList]);

  return (
    <StyledCashFrenzy
      backgroundSrc={backgroundSrc}
      backgroundColor={backgroundColor}
      inMobileMock={inMobileMock}
      crossIcon={crossIcon}
      style={{
        marginBottom: isSpecialOffer ? '18px' : '0',
      }}
      onClick={selectOffer}
    >
      <div className="container">
        {salePercentage > 0 && (
          <CashFrenzySaleBadge
            salePercentage={salePercentage}
            salePercentageDisplayType={salePercentageDisplayType}
          />
        )}
        {emblemBadge && (
          <div className="emblem">
            <img
              className="emblem-badge"
              alt="emblem-badge"
              src={emblemBadge.imageUrl}
            />
          </div>
        )}
        <div className="products">
          <div className="products-main">
            {mainProduct && (
              <img className="image" src={mainProduct.images.product} />
            )}
          </div>
          <div className="products-info">
            {mainProduct && mainProduct?.strokedSaleQuantity && (
              <div className="products-info__stroked">
                <AutoTextSize
                  maxFontSizePx={16}
                  minFontSizePx={6}
                  mode="oneline"
                  className="stroked"
                >
                  WAS {mainProduct?.strokedSaleQuantity}
                </AutoTextSize>
              </div>
            )}
            {(mainProduct || subProducts.length > 0) && (
              <div className="products-container">
                {mainProduct && mainProduct.quantityDisplay && (
                  <AutoTextSize
                    maxFontSizePx={18}
                    minFontSizePx={6}
                    mode="oneline"
                    className="products-container__quantity"
                  >
                    {mainProduct.quantityDisplay}
                  </AutoTextSize>
                )}
                {subProducts.length > 0 && (
                  <div className="sub-products">
                    {subProducts
                      .slice(0, 4)
                      .map(
                        (
                          { textFontColorHex, images, quantityDisplay },
                          index,
                        ) => (
                          <div className="sub-products__container">
                            <div
                              style={{ color: textFontColorHex }}
                              className="sub-products__container-item"
                              key={index}
                            >
                              <img className="image" src={images.product} />
                              <AutoTextSize
                                className="quantity"
                                maxFontSizePx={14}
                                minFontSizePx={4}
                                mode="oneline"
                              >
                                {quantityDisplay}
                              </AutoTextSize>
                            </div>
                          </div>
                        ),
                      )}
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsTooltipOpen(!isTooltipOpen);
                      }}
                      className="sub-products__tooltip"
                    >
                      <img className="icon" src={tooltipIcon} alt="tooltip" />
                    </div>
                    {isTooltipOpen && (
                      <div
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsTooltipOpen(false);
                        }}
                        className="tooltip-content"
                      >
                        Claim full benefits in-game after purchase
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {timer && isSpecialOffer && (
              <div className="timer">ALL OFFERS END IN: {timer}</div>
            )}
          </div>
        </div>
        <BundlePrice
          prices={prices}
          type={EBundlesInternalViewModel.CASH_FRENZY}
          buttonSuffixImage={buttonSuffixImage}
          buttonSuffixAnimation={buttonSuffixAnimation}
          isFree={isFree}
          buttonText={collectText}
          isTextAutoResized
        />
      </div>
    </StyledCashFrenzy>
  );
};
